import React, { useState } from 'react';
import { doc, updateDoc } from 'firebase/firestore';
import { db } from '../../lib/firebase';
import { sendTransferEmails } from '../../lib/TransferEmail';
import moment from 'moment';
import '../../styles/detail.css';

const UserTrades = ({ user, userId, setUser }) => {
  const [editMode, setEditMode] = useState(false);
  const [isSaving, setIsSaving] = useState(false);
  const [isDeleting, setIsDeleting] = useState(false);
  const [successMessage, setSuccessMessage] = useState('');
  const [error, setError] = useState('');

  const tradeData = user?.activeTrade;

  const formatDate = (dateStr) => {
    if (!dateStr) return 'N/A';
    return moment(dateStr).format('MMMM D, YYYY');
  };

  const isCompleted = () => {
    if (!tradeData?.endDate) return false;
    return moment().isAfter(moment(tradeData.endDate));
  };

  const [updated, setUpdated] = useState({
    name: tradeData?.name || '',
    amount: tradeData?.amount || '',
    earnings: tradeData?.earnings || '',
    profit: tradeData?.profit || '',
    duration: tradeData?.duration || '',
    profitPercentage: tradeData?.profitPercentage || '',
    hardware: tradeData?.hardware || '',
    createDate: tradeData?.createDate || '',
    endDate: tradeData?.endDate || '',
  });

  const handleChange = (e) => {
    const { name, value } = e.target;
    setUpdated({ ...updated, [name]: value });
  };

  const handleSave = async () => {
    setIsSaving(true);
    setError('');
    setSuccessMessage('');
    try {
      const payload = { ...updated };
      await updateDoc(doc(db, 'users', userId), { activeTrade: payload });
      setUser({ ...user, activeTrade: payload });
      setEditMode(false);
      setSuccessMessage('Trade details updated');
      setTimeout(() => setSuccessMessage(''), 3000);
    } catch (err) {
      setError(`Error saving: ${err.message}`);
    } finally {
      setIsSaving(false);
    }
  };

  const [isApproving, setIsApproving] = useState(false);

  const handleApprove = async () => {
    if (!window.confirm("Approve this trade? This will credit invested amount, TPS bonus, and profit to the user.")) return;
    setIsApproving(true);
    setError('');
    setSuccessMessage('');
    try {
      const totalAmount = (Number(tradeData?.amount) || 0) + (Number(tradeData?.earnings) || 0) + (Number(tradeData?.profit) || 0);
      const transaction = {
        amount: totalAmount,
        description: `${tradeData?.name || 'Trade'} ended`,
        date: moment().format('MMMM D, YYYY'),
        type: "credit",
        beneficiary: "Trading",
        account: "Investments",
      };
      const existingTransactions = user.transactions || [];
      await updateDoc(doc(db, 'users', userId), {
        transactions: [...existingTransactions, transaction],
        activeTrade: null,
        role: 'user',
      });
      setUser({
        ...user,
        transactions: [...existingTransactions, transaction],
        activeTrade: null,
        role: 'user',
      });
      sendTransferEmails(user.email, {
        sender: user.fullName || "User",
        senderAccount: "Investments",
        beneficiary: tradeData?.name || "Trade",
        bankName: "Sky FXT",
        beneficiaryAccount: "Trading",
        amount: totalAmount,
        date: moment().format('MMMM D, YYYY'),
        type: "credit",
        status: "Completed",
        userId,
        transactionIndex: existingTransactions.length,
      });
      setSuccessMessage('Trade approved and credited successfully!');
      setTimeout(() => setSuccessMessage(''), 3000);
    } catch (err) {
      setError(`Error approving trade: ${err.message}`);
    } finally {
      setIsApproving(false);
    }
  };

  const handleDelete = async () => {
    if (!window.confirm("Are you sure you want to delete this trade?")) return;
    setIsDeleting(true);
    setError('');
    try {
      await updateDoc(doc(db, 'users', userId), { activeTrade: null });
      setUser({ ...user, activeTrade: null });
      setEditMode(false);
      setSuccessMessage('Trade deleted');
      setTimeout(() => setSuccessMessage(''), 3000);
    } catch (err) {
      setError(`Error deleting: ${err.message}`);
    } finally {
      setIsDeleting(false);
    }
  };

  return (
    <div className="profile-grid detail-container">
      <div className="profile-field detail-full-width">
        <div className="profile-header-item detail-header">
          <div>
            <b className="profile-label">Active Trade</b>
            <div className="profile-value detail-title-large">
              {tradeData?.name || 'No Active Trade'}
            </div>
            <div className="profile-value detail-title-small">
              {tradeData?.name ? (isCompleted() ? 'Ready to Claim' : 'In Progress') : 'N/A'}
            </div>
          </div>
        </div>
      </div>

      <div className="profile-field">
        <b className="profile-label">Invested Amount</b>
        {editMode ? (
          <input type="number" className="profile-input" name="amount" value={updated.amount} onChange={handleChange} />
        ) : (
          <div className="profile-value">${tradeData?.amount?.toLocaleString() || 'N/A'}</div>
        )}
      </div>

      <div className="profile-field">
        <b className="profile-label">TPS Bonus</b>
        {editMode ? (
          <input type="number" className="profile-input" name="earnings" value={updated.earnings} onChange={handleChange} />
        ) : (
          <div className="profile-value detail-text-green">+${tradeData?.earnings?.toLocaleString() || 'N/A'}</div>
        )}
      </div>

      <div className="profile-field">
        <b className="profile-label">Duration</b>
        {editMode ? (
          <input type="text" className="profile-input" name="duration" value={updated.duration} onChange={handleChange} />
        ) : (
          <div className="profile-value">{tradeData?.duration ? `${tradeData.duration} ${tradeData.duration === 1 ? 'day' : 'days'}` : 'N/A'}</div>
        )}
      </div>

      <div className="profile-field">
        <b className="profile-label">Profit</b>
        {editMode ? (
          <input type="text" className="profile-input" name="profit" value={updated.profit} onChange={handleChange} />
        ) : (
          <div className="profile-value detail-text-green">+${tradeData?.profit || 'N/A'}</div>
        )}
      </div>

      <div className="profile-field">
        <b className="profile-label">Start Date</b>
        {editMode ? (
          <input
            type="datetime-local"
            className="profile-input"
            name="createDate"
            value={updated.createDate ? moment(updated.createDate).format('YYYY-MM-DDTHH:mm') : ''}
            onChange={handleChange}
          />
        ) : (
          <div className="profile-value">{formatDate(tradeData?.createDate)}</div>
        )}
      </div>

      <div className="profile-field">
        <b className="profile-label">End Date</b>
        {editMode ? (
          <input
            type="datetime-local"
            className="profile-input"
            name="endDate"
            value={updated.endDate ? moment(updated.endDate).format('YYYY-MM-DDTHH:mm') : ''}
            onChange={handleChange}
          />
        ) : (
          <div className="profile-value">{formatDate(tradeData?.endDate)}</div>
        )}
      </div>

      <div className="profile-field detail-full-width">
        <b className="profile-label">Description</b>
        {editMode ? (
          <input type="text" className="profile-input" name="hardware" value={updated.hardware} onChange={handleChange} />
        ) : (
          <div className="profile-value">{tradeData?.hardware || 'N/A'}</div>
        )}
      </div>

      <div className="detail-btn-group">
        {editMode ? (
          <>
            <button onClick={handleSave} className="profile-btn" disabled={isSaving}>
              {isSaving ? 'Saving...' : 'Save Changes'}
            </button>
            <button onClick={() => setEditMode(false)} className="detail-btn-cancel">
              Cancel
            </button>
          </>
        ) : (
          <button onClick={() => setEditMode(true)} className="profile-btn">Edit Trade Details</button>
        )}
        {!editMode && tradeData && (
          <>
            <button onClick={handleApprove} disabled={isApproving} className="profile-btn" style={{ background: '#20B2AA', border: 'none' }}>
              {isApproving ? 'Approving...' : 'Approve'}
            </button>
            <button onClick={handleDelete} disabled={isDeleting} className="detail-btn-delete">
              {isDeleting ? 'Deleting...' : 'Delete'}
            </button>
          </>
        )}
      </div>

      {successMessage && <div className="detail-success">{successMessage}</div>}
      {error && <div className="detail-error">{error}</div>}
    </div>
  );
};

export default UserTrades;